import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { ModalTokenExpiration } from "shared/ModalTokenExpiration";
import { Toaster } from "react-hot-toast";
import Footer from "shared/Footer/Footer";
import SiteHeader from "containers/SiteHeader";
import BannerHeader from "components/HeaderBannerAccount";
import { PrivateRoute } from "routers/PrivateRoute";
import { MetadataUserProvider } from "contexts/metadata-user-context";
import { useMedia } from "react-use";

interface MainTemplateProps {
  children: ReactNode;
}

export const MainTemplate: React.FC<MainTemplateProps> = ({ children }) => {
  const [openModal, setOpenModal] = useState(false);
  const [timeTokenExpiration] = useState(30 * 60 * 1000); // 30 minutos
  const { setSearchEngine, messageError } = useHotels();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const isMobile = useMedia("(max-width: 768px)");

  const destinationId = params.destination_id!;
  const dataModal = {
    destinationName: searchParams.get("dest") ?? "",
    checkInDate: searchParams.get("checkin") ?? "",
    destination: Number(destinationId),
    numNights: Number(searchParams.get("nights")) ?? 0,
    adults: Number(searchParams.get("adults")) ?? 0,
    children: Number(searchParams.get("children")) ?? undefined,
    agesChildren: searchParams.get("ages_children")
      ? searchParams.get("ages_children")?.split(",").map(Number)
      : undefined,
  };

  useEffect(() => {
    if (location.pathname === "/") {
      setSearchEngine(false);
    }

    if (isMobile) {
      setSearchEngine(true);
    }
  }, [location.pathname, setSearchEngine, isMobile]);

  const checkAndShowModal = useCallback(() => {
    const lastModalData: { time: string; date: string } | null = JSON.parse(localStorage.getItem('lastModalShownTime') || '{}');
    const currentTime = new Date().getTime();
    const currentDate = new Date().toDateString();

    if (!lastModalData) {
      localStorage.setItem('lastModalShownTime', JSON.stringify({ time: currentTime, date: currentDate }));
    } else {
      const { time: lastTime, date: lastDate } = JSON.parse(localStorage.getItem('lastModalShownTime') || '{}');
      
      if (currentDate !== lastDate) {
        localStorage.setItem('lastModalShownTime', JSON.stringify({ time: currentTime, date: currentDate }));
      }

      if (currentTime - parseInt(lastTime) >= timeTokenExpiration) {
        if (renderModal()) {
          setOpenModal(true);
        }
      }
    }

    const nextCheckTime = Math.max(timeTokenExpiration - (currentTime - parseInt(lastModalData?.time ?? '0')), 0);
    setTimeout(checkAndShowModal, nextCheckTime);
  }, [timeTokenExpiration]);

  useEffect(() => {
    const timeoutId = setTimeout(checkAndShowModal, timeTokenExpiration);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [checkAndShowModal]);

  const handleCloseModal = () => {
    setOpenModal(false);
    const currentTime = new Date().getTime();
    const currentDate = new Date().toDateString();
    localStorage.setItem('lastModalShownTime', JSON.stringify({ time: currentTime, date: currentDate }));
  };

  const renderModal = () => {
    if (location.pathname === "/") {
      return false;
    }

    if (location.pathname.includes("/home")) {
      return true;
    }

    if (location.pathname.includes("/hotel")) {
      return true;
    }

    return false;
  }

  return (
    <MetadataUserProvider>
      <PrivateRoute>
        <Toaster position="top-center" />
        <main className="w-full min-h-[calc(100svh-144px)]">
          <SiteHeader />
          <BannerHeader />
          {children}
        </main>
        <Footer />
      </PrivateRoute>
      {openModal && !messageError && renderModal() && (
        <ModalTokenExpiration setOpenModal={handleCloseModal} openModal={openModal} data={dataModal} />
      )}
    </MetadataUserProvider>
  );
};
